import { Dropdown, type MenuProps, Tooltip } from "antd";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AllergenType } from "../gql/generated/graphqlSchema.tsx";
import { BorderOutlined, CheckSquareFilled, ClearOutlined } from "@ant-design/icons";

type ItemType = NonNullable<MenuProps["items"]>[number];

const ALL_ALLERGENS = Object.values(AllergenType);
const allergenSchema = z.enum(ALL_ALLERGENS);

// t("allergens.CELERY")
// t("allergens.CRUSTACEANS")
// t("allergens.EGGS")
// t("allergens.FISH")
// t("allergens.GLUTEN")
// t("allergens.LUPIN")
// t("allergens.MILK")
// t("allergens.MOLLUSCS")
// t("allergens.MUSTARD")
// t("allergens.PEANUTS")
// t("allergens.SESAME")
// t("allergens.SOYBEANS")
// t("allergens.SULPHITES")
// t("allergens.TREE_NUTS")
// t("allergens.none")
// t("allergens.title")

const CLEAR_ALL_KEY = "clear-all";

type AllergenSelectProps = {
  readonly value: readonly AllergenType[];
  readonly onChange: (allergens: AllergenType[]) => void;
  readonly children: ReactNode;
};

export const AllergenSelect = ({ value, onChange, children }: AllergenSelectProps) => {
  const { t, i18n } = useTranslation();

  const selected = useMemo(() => new Set(value), [value]);

  const toggle = (allergen: AllergenType) => {
    const next = new Set(selected);
    if (next.has(allergen)) {
      next.delete(allergen);
    } else {
      next.add(allergen);
    }
    onChange([...next]);
  };

  // t("allergens.clearAll")
  const menuItems = useMemo<ItemType[]>(
    () => [
      ...ALL_ALLERGENS.map((allergen) => ({
        key: allergen,
        label: t(`allergens.${allergen}`),
        icon: selected.has(allergen) ? <CheckSquareFilled /> : <BorderOutlined />,
      })).sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
      { type: "divider" as const },
      { key: CLEAR_ALL_KEY, label: t("allergens.clearAll"), icon: <ClearOutlined /> },
    ],
    [t, i18n.language, selected],
  );

  const tooltipContent = useMemo(() => {
    if (value.length === 0) {
      return (
        <>
          {t("allergens.title")}: {t("allergens.none")}
        </>
      );
    }
    const names = value.map((a) => t(`allergens.${a}`)).sort((a, b) => a.localeCompare(b, i18n.language));
    return (
      <>
        {t("allergens.title")}:
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </>
    );
  }, [value, t, i18n.language]);

  return (
    <Tooltip destroyOnHidden title={tooltipContent}>
      <Dropdown
        arrow
        destroyOnHidden
        menu={{
          items: menuItems,
          selectedKeys: [...selected],
          selectable: true,
          multiple: true,
          onClick: ({ key }) => {
            if (key === CLEAR_ALL_KEY) {
              onChange([]);
              return;
            }
            const parsed = allergenSchema.safeParse(key);
            if (parsed.success) toggle(parsed.data);
          },
        }}
        trigger={["click"]}
      >
        {children}
      </Dropdown>
    </Tooltip>
  );
};
